import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Banknote, CheckCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from './utils';
import { toNumber, safeFixed } from '../../utils/formatters';

// Payment Dialog for adding payments
export function PaymentDialog({
  open,
  onOpenChange,
  reservation,
  paymentSummary,
  paymentForm,
  setPaymentForm,
  paymentMethods,
  currencies = [],
  exchangeRates = {},
  onSave
}) {
  if (!reservation) return null;

  // Default currency to reservation currency
  const selectedCurrency = paymentForm.currency || reservation.currency || 'MAD';
  
  // Calculate converted amount in MAD
  const amount = toNumber(paymentForm.amount);
  const rate = toNumber(exchangeRates[selectedCurrency], 1);
  const amountInMad = selectedCurrency !== 'MAD' ? safeFixed(amount * rate, 2) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-green-600" />
            Enregistrer un paiement
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Summary */}
          <div className="p-3 bg-muted rounded-lg text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reste a encaisser:</span>
              <span className="font-bold text-red-600">
                {formatCurrency(paymentSummary.remaining, reservation?.currency)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Montant *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label>Devise</Label>
              <Select 
                value={selectedCurrency} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, currency: v })}
              >
                <SelectTrigger data-testid="payment-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.length > 0 ? (
                    currencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol || c.code} {c.code}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="MAD">MAD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Show conversion info when currency is not MAD */}
          {amountInMad && amount > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div className="flex justify-between items-center">
                <span className="text-blue-700">Equivalent en MAD:</span>
                <span className="font-bold text-blue-800">{amountInMad} MAD</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Taux: 1 {selectedCurrency} = {safeFixed(rate, 4)} MAD
              </p>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={() => setPaymentForm({ 
                ...paymentForm, 
                amount: paymentSummary.remaining.toString(),
                currency: reservation?.currency || 'MAD',
                payment_type: 'FULL' 
              })}
            >
              Tout encaisser
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={() => setPaymentForm({ 
                ...paymentForm, 
                amount: safeFixed(toNumber(paymentSummary.remaining) / 2, 2),
                currency: reservation?.currency || 'MAD',
                payment_type: 'DEPOSIT' 
              })}
            >
              50%
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mode de paiement</Label>
              <Select 
                value={paymentForm.method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(method => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select 
                value={paymentForm.payment_type} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEPOSIT">Acompte</SelectItem>
                  <SelectItem value="BALANCE">Solde</SelectItem>
                  <SelectItem value="FULL">Paiement total</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Date du paiement</Label>
            <Input
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              placeholder="Reference, commentaire..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={onSave} className="bg-green-600 hover:bg-green-700" data-testid="save-payment-btn">
            <CheckCircle className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Payment List Component
export function PaymentList({
  payments,
  paymentMethods,
  paymentSummary,
  reservation,
  onAddPayment,
  onDeletePayment
}) {
  return (
    <div className="space-y-4">
      {/* Payment Summary */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-amber-50 to-green-50 rounded-lg border">
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Total à encaisser</p>
          <p className="text-xl font-bold">
            {formatCurrency(paymentSummary.total_amount, reservation?.currency)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Déjà encaissé</p>
          <p className="text-xl font-bold text-green-600">
            {formatCurrency(paymentSummary.total_paid, reservation?.currency)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Reste à encaisser</p>
          <p className={`text-xl font-bold ${paymentSummary.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatCurrency(paymentSummary.remaining, reservation?.currency)}
          </p>
        </div>
      </div>

      {/* Add Payment Button */}
      {paymentSummary.remaining > 0 && (
        <div className="flex justify-end">
          <Button onClick={onAddPayment} data-testid="add-payment-btn">
            <Banknote className="w-4 h-4 mr-2" />
            Encaisser un paiement
          </Button>
        </div>
      )}

      {/* Payment History */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Historique des paiements</p>
        {payments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <Banknote className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucun paiement enregistré</p>
            <p className="text-xs mt-1">Enregistrez les acomptes et paiements reçus</p>
          </div>
        ) : (
          <div className="space-y-2">
            {payments.map((payment) => (
              <div 
                key={payment.id} 
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg group hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    payment.payment_type === 'FULL' ? 'bg-green-100' : 
                    payment.payment_type === 'BALANCE' ? 'bg-blue-100' : 'bg-amber-100'
                  }`}>
                    {payment.payment_type === 'FULL' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Banknote className="w-4 h-4 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">
                      {payment.payment_type === 'DEPOSIT' ? 'Acompte' : 
                       payment.payment_type === 'BALANCE' ? 'Solde' : 'Paiement total'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(payment.payment_date), 'dd/MM/yyyy')} • 
                      {paymentMethods.find(m => m.value === payment.method)?.label || payment.method}
                      {payment.notes && ` • ${payment.notes}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-lg font-bold text-green-600">
                      +{formatCurrency(payment.amount, payment.currency)}
                    </span>
                    {/* Show MAD conversion if different currency */}
                    {payment.currency !== 'MAD' && payment.amount_mad && (
                      <p className="text-xs text-muted-foreground">
                        = {formatCurrency(payment.amount_mad, 'MAD')}
                      </p>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 text-red-500"
                    onClick={() => onDeletePayment(payment.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fully Paid Badge */}
      {paymentSummary.is_fully_paid && (
        <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="font-medium text-green-700">Réservation entièrement payée</p>
        </div>
      )}
    </div>
  );
}
